import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Combine, Layers } from "lucide-react";
import StatusBadge from "@/components/app/StatusBadge";

export default function Duplicates() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/duplicates").then(({ data }) => setGroups(data.items || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const merge = async (group, keep) => {
    const others = group.assets.filter((a) => a.id !== keep).map((a) => a.id);
    try {
      await api.post("/assets/merge", { keep_asset_id: keep, merge_asset_ids: others });
      toast.success(`Merged ${others.length} into golden record`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    }
  };

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Data Quality</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Duplicates</h1>
        <p className="text-sm text-slate-500 mt-0.5">Detected duplicate golden records. Choose which to keep — history is preserved on merge.</p>
      </div>

      {loading ? <div className="text-sm text-slate-500">Loading…</div>
      : groups.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center">
          <Layers className="h-6 w-6 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-600">No duplicates detected in your tenant.</div>
        </div>
      ) : groups.map((g) => (
        <div key={g.serial} className="bg-white border border-slate-200 rounded-md">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Duplicate group</div>
              <div className="font-mono text-sm text-slate-900">Serial {g.serial} · {g.count} records</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
            {g.assets.map((a) => (
              <div key={a.id} className="border border-slate-200 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs text-slate-500">{a.asset_tag}</div>
                  <StatusBadge value={a.lifecycle_state} />
                </div>
                <div className="mt-1 text-sm text-slate-900">{a.brand} {a.model}</div>
                <div className="text-xs text-slate-500">Created {new Date(a.created_at).toLocaleDateString()}</div>
                <Button size="sm" className="mt-2 bg-slate-900 hover:bg-slate-800 text-white" onClick={() => merge(g, a.id)}>
                  <Combine className="h-4 w-4 mr-1.5" /> Keep this — merge others
                </Button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
